import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Printer, Package, DollarSign, TrendingUp } from 'lucide-react';
import { useVat } from '../../contexts/VatContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';
import { calculateUnitCostWithVat } from '../../utils/calculations';
import { materialsAPI } from '../../utils/api';
import AllergenBadge from '../Allergens/AllergenBadge.jsx';

const MaterialDetails = ({ material, onClose }) => {
  const { t, i18n } = useTranslation();
  const { vatPercentage } = useVat();
  const { formatCurrency } = useCurrency();
  const { theme } = useTheme();
  const [categories, setCategories] = React.useState([]);
  const [usageStats, setUsageStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [logoUrl, setLogoUrl] = React.useState('');

  React.useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const categoriesData = await materialsAPI.getCategories();
        setCategories(categoriesData);
        
        try {
          const stats = await materialsAPI.getUsageStats(material.id);
          setUsageStats(stats);
        } catch (error) {
          console.error('Error loading usage stats:', error);
          // Set default values when stats are not available
          setUsageStats({
            recipeCount: 0,
            totalUsage: 0,
            averageUsage: 0,
            recipeDetails: []
          });
        }

        // Fetch logo
        try {
          const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
          const logoResponse = await fetch(`${API_BASE_URL}/preferences/logo`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (logoResponse.ok) {
            const logoData = await logoResponse.json();
            if (logoData.success && logoData.value && logoData.value.trim() !== '') {
              setLogoUrl(logoData.value);
            }
          }
        } catch (error) {
          console.error('Error fetching logo:', error);
          // Continue without logo if fetch fails
        }
      } catch (error) {
        console.error('Error loading material details:', error);
        setError('Failed to load material details');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [material.id]);

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name_en : t('materials.unnamed');
  };

  const calculateUnitCost = () => {
    return calculateUnitCostWithVat(material, vatPercentage);
  };

  const handlePrint = async () => {
    // Fetch logo before printing to ensure we have the latest
    let currentLogoUrl = logoUrl;
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const logoResponse = await fetch(`${API_BASE_URL}/preferences/logo`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (logoResponse.ok) {
        const logoData = await logoResponse.json();
        if (logoData.success && logoData.value && logoData.value.trim() !== '') {
          currentLogoUrl = logoData.value;
        }
      }
    } catch (error) {
      console.error('Error fetching logo for print:', error);
      // Continue without logo if fetch fails
    }

    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (printWindow) {
      // Generate allergen badges HTML
      const allergensHtml = material.allergens && material.allergens.length > 0 
        ? material.allergens.map(allergen => 
            `<span style="display: inline-block; background: #fef3c7; color: #92400e; padding: 2px 5px; border-radius: 2px; font-size: 9px; margin: 1px;">${allergen.replace('_', ' ')}</span>`
          ).join(' ')
        : '<span style="color: #6b7280;">-</span>';

      // Generate recipe details HTML
      const recipeDetailsHtml = usageStats?.recipeDetails && usageStats.recipeDetails.length > 0
        ? usageStats.recipeDetails.map(recipe => 
            `<div style="background: #f0f9ff; padding: 3px 5px; margin: 2px 0; border-radius: 2px; border: 1px solid #bae6fd;">
              <div style="font-weight: 600; color: #0c4a6e; font-size: 9px;">${recipe.recipe_name_en}</div>
              <div style="color: #0369a1; font-size: 8px;">${recipe.quantity} ${material.sub_unit || 'piece'}</div>
            </div>`
          ).join('')
        : '<div style="color: #6b7280; font-style: italic; font-size: 9px;">No recipes found</div>';

      // Create the print document
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Material Details - ${material.name_en}</title>
          <style>
            @page { 
              margin: 0.25in; 
              size: A4; 
            }
            
            * {
              box-sizing: border-box;
            }
            
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 0;
              background: white;
              color: #000;
              font-size: 10px;
              line-height: 1.3;
            }
            
            .header {
              text-align: center;
              margin-bottom: 8px;
              border-bottom: 1px solid #000;
              padding-bottom: 6px;
            }
            
            .header img {
              max-height: 40px !important;
              max-width: 150px !important;
              margin-bottom: 4px !important;
            }
            
            .title {
              font-size: 18px;
              font-weight: bold;
              margin: 0;
              color: #111827;
              line-height: 1.2;
            }
            
            .subtitle {
              font-size: 12px;
              color: #6b7280;
              margin: 2px 0;
              line-height: 1.2;
            }
            
            .date {
              font-size: 9px;
              color: #9ca3af;
              margin-top: 2px;
            }
            
            .section {
              margin-bottom: 8px;
              page-break-inside: avoid;
            }
            
            .section-title {
              background: #f3f4f6;
              padding: 4px 6px;
              font-weight: bold;
              font-size: 11px;
              border-left: 2px solid #374151;
              margin-bottom: 6px;
              color: #111827;
            }
            
            .grid-2 {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 6px;
              margin-bottom: 6px;
            }
            
            .grid-3 {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 4px;
              margin-bottom: 6px;
            }
            
            .grid-4 {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr 1fr;
              gap: 4px;
              margin-bottom: 6px;
            }
            
            .info-card {
              background: #f9fafb;
              border: 1px solid #e5e7eb;
              padding: 5px;
              border-radius: 3px;
            }
            
            .info-label {
              font-size: 9px;
              color: #6b7280;
              text-transform: uppercase;
              font-weight: 600;
              margin-bottom: 3px;
            }
            
            .info-value {
              font-size: 10px;
              color: #111827;
            }
            
            .info-item {
              margin-bottom: 2px;
              display: flex;
              justify-content: space-between;
              font-size: 9px;
            }
            
            .info-item strong {
              font-weight: 600;
              margin-right: 4px;
            }
            
            .cost-card {
              background: #ecfdf5;
              border: 1px solid #d1fae5;
              padding: 5px;
              border-radius: 3px;
              text-align: center;
            }
            
            .cost-label {
              font-size: 9px;
              color: #059669;
              font-weight: 600;
              margin-bottom: 2px;
            }
            
            .cost-value {
              font-size: 12px;
              font-weight: bold;
              color: #047857;
            }
            
            .spec-card {
              background: #eff6ff;
              border: 1px solid #bfdbfe;
              padding: 5px;
              border-radius: 3px;
              text-align: center;
            }
            
            .spec-label {
              font-size: 9px;
              color: #1d4ed8;
              font-weight: 600;
              margin-bottom: 2px;
            }
            
            .spec-value {
              font-size: 11px;
              font-weight: bold;
              color: #1e40af;
            }
            
            .usage-header {
              background: #fef3c7;
              border: 1px solid #fbbf24;
              padding: 5px;
              border-radius: 3px;
              display: flex;
              gap: 10px;
              margin-bottom: 6px;
            }
            
            .usage-item {
              flex: 1;
              text-align: center;
            }
            
            .usage-label {
              font-size: 9px;
              color: #92400e;
              font-weight: 600;
              margin-bottom: 2px;
            }
            
            .usage-value {
              font-size: 13px;
              font-weight: bold;
              color: #b45309;
            }
            
            .recipe-details {
              max-height: none !important;
              overflow-y: visible !important;
              overflow: visible !important;
            }
            
            .recipe-details > div {
              margin-bottom: 3px !important;
              padding: 3px 4px !important;
              font-size: 9px !important;
            }
            
            .image-container {
              text-align: center;
              margin: 6px 0;
            }
            
            .material-image {
              max-width: 80px;
              max-height: 80px;
              border-radius: 3px;
              border: 1px solid #e5e7eb;
            }
            
            .two-column {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 6px;
            }
            
            .full-width {
              grid-column: 1 / -1;
            }
            
            .highlight {
              background: #fef3c7;
              padding: 1px 3px;
              border-radius: 2px;
            }
            
            /* Compact table styles */
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 9px;
              margin: 4px 0;
            }
            
            table th, table td {
              padding: 3px 4px;
              border: 1px solid #e5e7eb;
              text-align: left;
            }
            
            table th {
              background: #f3f4f6;
              font-weight: 600;
              font-size: 9px;
            }
          </style>
        </head>
        <body>
          <!-- Header -->
          <div class="header">
            ${currentLogoUrl ? `<div style="text-align: center; margin-bottom: 4px;"><img src="${currentLogoUrl}" alt="Logo" style="max-height: 40px; max-width: 150px; object-fit: contain;" /></div>` : ''}
            <h1 class="title">${material.name_en}</h1>
            <div class="subtitle">${material.name_ar}</div>
            <div class="date">Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div>
          </div>

          <!-- Material Overview -->
          <div class="section">
            <div class="section-title">📦 Material Overview</div>
            
            ${material.image_url ? `
            <div class="image-container">
              <img src="${material.image_url}" alt="${material.name_en}" class="material-image">
            </div>
            ` : ''}
            
            <div class="two-column">
              <div class="info-card">
                <div class="info-label">Basic Information</div>
                <div style="margin-top: 3px;">
                  <div class="info-item">
                    <span><strong>English:</strong></span>
                    <span>${material.name_en}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Arabic:</strong></span>
                    <span>${material.name_ar}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Category:</strong></span>
                    <span>${getCategoryName(material.category_id)}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Brand:</strong></span>
                    <span>${material.brand_name || 'N/A'}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Supplier:</strong></span>
                    <span>${material.supplier || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              <div class="info-card">
                <div class="info-label">Nutritional Information</div>
                <div style="margin-top: 3px;">
                  <div class="info-item">
                    <span><strong>Calories:</strong></span>
                    <span>${material.calories ? `${Math.round(material.calories)} kcal` : '-'}</span>
                  </div>
                  <div class="info-item">
                    <span><strong>Serving:</strong></span>
                    <span>${material.serving_size || '-'}</span>
                  </div>
                  <div style="margin-top: 3px;">
                    <div class="info-label">Allergens</div>
                    <div style="margin-top: 2px;">${allergensHtml}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Specifications -->
          <div class="section">
            <div class="section-title">📏 Specifications</div>
            <div class="grid-4">
              <div class="spec-card">
                <div class="spec-label">Main Unit</div>
                <div class="spec-value">${material.main_unit || 'N/A'}</div>
              </div>
              <div class="spec-card">
                <div class="spec-label">Sub Unit</div>
                <div class="spec-value">${material.sub_unit || 'N/A'}</div>
              </div>
              <div class="spec-card">
                <div class="spec-label">Qty per Main Unit</div>
                <div class="spec-value">${material.quantity_per_main_unit || 'N/A'}</div>
              </div>
              <div class="spec-card">
                <div class="spec-label">Qty per Piece</div>
                <div class="spec-value">${material.quantity_per_piece || 'N/A'}</div>
              </div>
            </div>
          </div>

          <!-- Cost Breakdown -->
          <div class="section">
            <div class="section-title">💰 Cost Breakdown</div>
            <div class="grid-3">
              <div class="cost-card">
                <div class="cost-label">Price without VAT</div>
                <div class="cost-value">${formatCurrency(material.price_without_vat || 0)}</div>
              </div>
              <div class="cost-card">
                <div class="cost-label">Price with VAT</div>
                <div class="cost-value">${formatCurrency(material.price_with_vat || 0)}</div>
              </div>
              <div class="cost-card">
                <div class="cost-label">Calculated Unit Cost</div>
                <div class="cost-value">${formatCurrency(unitCost)}</div>
              </div>
            </div>
          </div>

          <!-- Usage Statistics -->
          <div class="section">
            <div class="section-title">📊 Usage Statistics</div>
            
            <div class="usage-header">
              <div class="usage-item">
                <div class="usage-label">Used in Recipes</div>
                <div class="usage-value">${usageStats?.recipeCount || 0}</div>
              </div>
              <div class="usage-item">
                <div class="usage-label">Total Quantity Used</div>
                <div class="usage-value">${usageStats?.totalUsage || 0}</div>
              </div>
            </div>
            
            <div class="info-card">
              <div class="info-label">Recipe Details</div>
              <div class="recipe-details" style="margin-top: 3px;">
                ${recipeDetailsHtml}
              </div>
            </div>
          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    } else {
      // Fallback to regular print if new window fails
    window.print();
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card rounded-xl shadow-lg border border-border max-w-4xl w-full mx-4">
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">{t('common.loading')}...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card rounded-xl shadow-lg border border-border max-w-4xl w-full mx-4">
          <div className="flex justify-between items-center mb-4 p-6">
            <h2 className="text-lg font-semibold text-card-foreground">{t('common.error')}</h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-card-foreground">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="text-center py-8 px-6">
            <p className="text-destructive">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const unitCost = calculateUnitCost();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`flex justify-between items-center mb-6 p-6 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <div className={i18n.language === 'ar' ? 'text-right' : 'text-left'}>
            <h2 className="text-2xl font-bold text-card-foreground">{material.name_en}</h2>
            <p className="text-muted-foreground mt-1">{material.name_ar}</p>
          </div>
          <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <button 
              onClick={handlePrint} 
              className={`inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}
            >
              <Printer className={`h-4 w-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
{t('common.print')}
            </button>
            <button 
              onClick={onClose} 
              className="text-muted-foreground hover:text-card-foreground p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Print Content */}
        <div className="print-content space-y-6 px-6 pb-6">
          {/* Print Header */}
          <div className="hidden print:block text-center mb-6">
            <h1 className="text-2xl font-bold mb-2 text-card-foreground">{material.name_en}</h1>
            <p className="text-muted-foreground">{material.name_ar}</p>
            <div className="border-t border-border mt-4 pt-4">
              <p className="text-sm text-muted-foreground">
{t('materials.generatedOn')}: {new Date().toLocaleDateString()} {t('common.at')} {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>

          {/* Material Overview Card */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center mr-4">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-card-foreground">{t('materials.materialOverview')}</h3>
                <p className="text-sm text-muted-foreground">{t('materials.basicInformationAndSpecifications')}</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Material Image */}
              {material.image_url && (
                <div className="flex justify-center">
                  <div className="relative">
                    <img
                      src={material.image_url}
                      alt={material.name_en}
                      className="w-32 h-32 object-cover rounded-lg shadow-sm border border-border"
                    />
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
{t('materials.available')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Material Information */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-muted/50 rounded-lg p-4 border border-border">
                  <h4 className="text-sm font-medium text-card-foreground mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
{t('materials.basicInformation')}
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">English:</span>
                      <span className="text-sm font-medium text-card-foreground">{material.name_en}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">Arabic:</span>
                      <span className="text-sm font-medium text-card-foreground">{material.name_ar}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">Category:</span>
                      <span className="text-sm font-medium text-card-foreground">{getCategoryName(material.category_id)}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">Brand:</span>
                      <span className="text-sm font-medium text-card-foreground">{material.brand_name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-muted-foreground w-24">Supplier:</span>
                      <span className="text-sm font-medium text-card-foreground">{material.supplier || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Nutritional Information */}
                <div className="bg-accent/30 rounded-lg p-4 border border-accent/50">
                  <h4 className="text-sm font-medium text-accent-foreground mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-accent-foreground/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
{t('materials.nutritionalInformation')}
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-sm text-accent-foreground/70 w-24">Calories:</span>
                      <span className="text-sm font-medium text-accent-foreground">
                        {material.calories ? `${Math.round(material.calories)} kcal` : '-'}
                      </span>
                      </div>
                    <div className="flex items-center">
                      <span className="text-sm text-accent-foreground/70 w-24">Serving:</span>
                      <span className="text-sm font-medium text-accent-foreground">
                        {material.serving_size || '-'}
                      </span>
                        </div>
                    <div className="flex items-start">
                      <span className="text-sm text-accent-foreground/70 w-24 mt-1">Allergens:</span>
                      <div className="flex-1">
                        {material.allergens && material.allergens.length > 0 ? (
                          <AllergenBadge 
                            allergens={material.allergens} 
                            variant="warning" 
                            size="xs"
                            maxDisplay={5}
                          />
                        ) : (
                          <span className="text-sm font-medium text-accent-foreground">-</span>
                      )}
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Specifications Card */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-chart-3/20 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-chart-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-card-foreground">{t('materials.specifications')}</h3>
                <p className="text-sm text-muted-foreground">{t('materials.measurementAndQuantityDetails')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-chart-1/10 p-4 rounded-lg border border-chart-1/30">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-chart-1/20 rounded-lg flex items-center justify-center mr-3">
                    <Package className="w-4 h-4 text-chart-1" />
                  </div>
                  <h4 className="font-medium text-chart-1">{t('materials.mainUnit')}</h4>
                </div>
                <p className="text-xl font-bold text-chart-1">{material.main_unit || 'N/A'}</p>
                <p className="text-xs text-chart-1/70 mt-1">{t('materials.primaryUnitOfMeasurement')}</p>
              </div>
              
              <div className="bg-chart-2/10 p-4 rounded-lg border border-chart-2/30">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-chart-2/20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-chart-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <h4 className="font-medium text-chart-2">{t('materials.subUnit')}</h4>
                </div>
                <p className="text-xl font-bold text-chart-2">{material.sub_unit || 'N/A'}</p>
                <p className="text-xs text-chart-2/70 mt-1">{t('materials.secondaryUnitOfMeasurement')}</p>
              </div>
              
              <div className="bg-chart-3/10 p-4 rounded-lg border border-chart-3/30">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-chart-3/20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-chart-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <h4 className="font-medium text-chart-3">{t('materials.qtyPerMainUnit')}</h4>
                </div>
                <p className="text-xl font-bold text-chart-3">{material.quantity_per_main_unit || 'N/A'}</p>
                <p className="text-xs text-chart-3/70 mt-1">{t('materials.quantityInMainUnit')}</p>
              </div>
              
              <div className="bg-chart-4/10 p-4 rounded-lg border border-chart-4/30">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-chart-4/20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-chart-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <h4 className="font-medium text-chart-4">{t('materials.qtyPerPiece')}</h4>
                </div>
                <p className="text-xl font-bold text-chart-4">{material.quantity_per_piece || 'N/A'}</p>
                <p className="text-xs text-chart-4/70 mt-1">{t('materials.quantityPerIndividualPiece')}</p>
              </div>
            </div>
          </div>

          {/* Cost Breakdown Card */}
          <div className="bg-card rounded-xl shadow-sm border border-border p-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-chart-2/20 rounded-lg flex items-center justify-center mr-4">
                <DollarSign className="w-5 h-5 text-chart-2" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-card-foreground">{t('materials.costBreakdown')}</h3>
                <p className="text-sm text-muted-foreground">{t('materials.detailedCostAnalysisAndCalculations')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-chart-2/10 p-4 rounded-lg border border-chart-2/30">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-chart-2/20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-chart-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <h4 className="font-medium text-chart-2">{t('materials.priceWithoutVat')}</h4>
                </div>
                <p className="text-xl font-bold text-chart-2">{formatCurrency(material.price_without_vat || 0)}</p>
                <p className="text-xs text-chart-2/70 mt-1">{t('materials.priceExcludingVat')}</p>
              </div>
              
              <div className="bg-chart-3/10 p-4 rounded-lg border border-chart-3/30">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-chart-3/20 rounded-lg flex items-center justify-center mr-3">
                    <DollarSign className="w-4 h-4 text-chart-3" />
                  </div>
                  <h4 className="font-medium text-chart-3">{t('materials.priceWithVat')}</h4>
                </div>
                <p className="text-xl font-bold text-chart-3">{formatCurrency(material.price_with_vat || 0)}</p>
                <p className="text-xs text-chart-3/70 mt-1">{t('materials.priceIncludingVat')}</p>
              </div>
              
              <div className="bg-chart-4/10 p-4 rounded-lg border border-chart-4/30">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-chart-4/20 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-chart-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="font-medium text-chart-4">{t('materials.calculatedUnitCost')}</h4>
                </div>
                <p className="text-xl font-bold text-chart-4">{formatCurrency(unitCost)}</p>
                <p className="text-xs text-chart-4/70 mt-1">{t('materials.costPerSmallestUnit')}</p>
              </div>
            </div>
          </div>

          {/* Usage Statistics Card */}
            <div className="bg-card rounded-xl shadow-sm border border-border p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-chart-5/20 rounded-lg flex items-center justify-center mr-4">
                  <TrendingUp className="w-5 h-5 text-chart-5" />
                </div>
              <div className="flex-1">
                  <h3 className="text-lg font-semibold text-card-foreground">{t('materials.usageStatistics')}</h3>
                  <p className="text-sm text-muted-foreground">{t('materials.howThisMaterialIsUsed')}</p>
              </div>
              <div className="bg-chart-2/10 p-3 rounded-lg border border-chart-2/30 ml-4">
                <div className="text-center">
                  <p className="text-lg font-bold text-chart-2">{usageStats?.recipeCount || 0}</p>
                  <p className="text-xs text-chart-2/70">{t('materials.usedInRecipes')}</p>
                </div>
              </div>
              <div className="bg-chart-3/10 p-3 rounded-lg border border-chart-3/30 ml-3">
                <div className="text-center">
                  <p className="text-lg font-bold text-chart-3">{usageStats?.totalUsage || 0}</p>
                  <p className="text-xs text-chart-3/70">{t('materials.totalQuantityUsed')}</p>
                    </div>
                  </div>
                </div>
                
            <div className="grid grid-cols-1 gap-4">
              
              <div className="bg-chart-1/10 p-4 rounded-lg border border-chart-1/30 md:col-span-2 lg:col-span-1">
                <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-chart-1/20 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-chart-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                  <h4 className="font-medium text-chart-1">{t('materials.recipeDetails')}</h4>
                  </div>
                <div className="space-y-2">
                  {usageStats?.recipeDetails && usageStats.recipeDetails.length > 0 ? (
                    usageStats.recipeDetails.map((recipe, index) => (
                      <div key={index} className="text-xs bg-card rounded p-2 border border-chart-1/30">
                        <div className="font-medium text-chart-1">ID: {recipe.recipe_id} - {recipe.recipe_name_en}</div>
                        <div className="text-chart-1/70">
                          {recipe.quantity} {material.sub_unit || 'piece'}
                 </div>
               </div>
                    ))
                  ) : (
                    <div className="text-xs text-chart-1/70 italic">{t('materials.noRecipesFound')}</div>
                  )}
              </div>
              </div>
            </div>
          </div>


          {/* Print Footer */}
          <div className="hidden print:block mt-8 pt-4 border-t border-border">
            <div className="text-center text-sm text-muted-foreground">
              <p>{t('materials.dishCostingSystem')}</p>
              <p>{t('materials.pageOf')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialDetails; 